import { useKatpackStore } from "../stores/useKatpackStore";

let interceptorInstalled = false;

export function setupFetchAuthInterceptor() {
  if (interceptorInstalled) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;

  const originalFetch = window.fetch.bind(window);
  let refreshPromise = null;

  const persistTokens = (payload) => {
    if (!payload) return;
    const state = useKatpackStore.getState();
    if (payload.access_token && state.setJwtToken) state.setJwtToken(payload.access_token);
    if (Object.prototype.hasOwnProperty.call(payload, "refresh_token") && state.setRefreshToken) {
      state.setRefreshToken(payload.refresh_token || null);
    }
  };

  const clearTokens = () => {
    const state = useKatpackStore.getState();
    state.clearAuthTokens?.();
  };

  const resolveUrl = (input) => {
    if (input instanceof Request) return input.url;
    if (typeof input === "string") {
      try { return new URL(input, window.location.origin).toString(); } catch { return window.location.href; }
    }
    if (input instanceof URL) return input.toString();
    return window.location.href;
  };

  const isAuthEndpoint = (url) => {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const last = path.split("/").filter(Boolean).at(-1);
      return last === "login" || last === "refresh";
    } catch { return false; }
  };

  const maybeCaptureTokens = async (response, url) => {
    if (!response || !isAuthEndpoint(url)) return;
    try {
      const data = await response.json();
      persistTokens(data);
    } catch { /* non-fatal */ }
  };

  const prepareFetchArgs = (input, init, token) => {
    const url = resolveUrl(input);
    if (input instanceof Request) {
      if (!token) return { input, init, url };
      const headers = new Headers(input.headers || {});
      if (!headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
        return { input: new Request(input, { headers }), init, url };
      }
      return { input, init, url };
    }
    const headers = new Headers(init?.headers || {});
    if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
    return { input, init: { ...(init || {}), headers }, url };
  };

  const requestTokenRefresh = async (failingUrl) => {
    const refreshToken = useKatpackStore.getState().instance?.refreshToken;
    if (!refreshToken) {
      clearTokens();
      window.dispatchEvent(new CustomEvent("sessionExpired", { detail: { reason: "No refresh token", timestamp: Date.now() } }));
      return false;
    }

    if (!refreshPromise) {
      const apiBaseUrl = useKatpackStore.getState().instance?.apiBaseUrl;
      const base = apiBaseUrl?.replace(/\/+$/, "") ?? new URL(failingUrl, window.location.origin).origin;
      refreshPromise = originalFetch(`${base}/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
        .then(async (resp) => {
          if (!resp.ok) throw new Error(`Refresh failed: ${resp.status}`);
          persistTokens(await resp.json());
          return true;
        })
        .catch((err) => {
          clearTokens();
          window.dispatchEvent(new CustomEvent("sessionExpired", { detail: { reason: err.message, timestamp: Date.now() } }));
          return false;
        })
        .finally(() => { refreshPromise = null; });
    }

    return refreshPromise;
  };

  window.fetch = async (input, init = {}) => {
    const token = useKatpackStore.getState().instance?.jwtToken;
    const first = prepareFetchArgs(input, init, token);
    let response = await originalFetch(first.input, first.init);
    await maybeCaptureTokens(response.clone(), first.url);

    if (response.status !== 401 || isAuthEndpoint(first.url)) return response;

    const refreshed = await requestTokenRefresh(first.url);
    if (!refreshed) return response;

    const newToken = useKatpackStore.getState().instance?.jwtToken;
    const retry = prepareFetchArgs(input, init, newToken);
    const retryResponse = await originalFetch(retry.input, retry.init);
    await maybeCaptureTokens(retryResponse.clone(), retry.url);
    return retryResponse;
  };

  interceptorInstalled = true;
}
